import React, { useEffect, useMemo, useState } from "react";
import {
  createEntityRole,
  generateEntityRoleSeedsForAccount,
  generateMarketProfileForAccount,
  getEntityRoles,
  getLatestMarketProfile,
  getLatestBrandGuidance,
  saveBrandGuidance,
  saveMarketProfile,
  updateEntityRole,
  updateMarketProfileStatus,
} from "../../lib/competitiveSetupApi";
import type {
  AccountEntityRole,
  AccountMarketProfile,
  AccountBrandGuidance,
  EntityRole,
  ScoreTreatment,
} from "../../types/competitiveSetup";

const ENTITY_ROLES: EntityRole[] = [
  "direct_competitor",
  "adjacent_competitor",
  "platform",
  "partner",
  "integration",
  "product",
  "customer",
  "publication",
  "generic_tool",
  "suppress",
  "unknown",
];

const SCORE_TREATMENTS: ScoreTreatment[] = [
  "include_primary",
  "include_secondary",
  "show_only",
  "suppress_from_scoring",
  "suppress_everywhere",
];

type Props = {
  accountId: string;
  isPartnerMode?: boolean;
  onChanged?: () => void | Promise<void>;
};

const fieldLabelStyle: React.CSSProperties = {
  display: "grid",
  gap: 4,
  color: "#334155",
  fontSize: 12,
  fontWeight: 800,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: 10,
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 800,
  color: "#64748b",
  marginBottom: 4,
};

function parseCsv(value: string) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function toCsv(value: unknown) {
  return Array.isArray(value) ? value.join(", ") : "";
}

function FieldValue({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: 14, color: "#0f172a", whiteSpace: "pre-wrap" }}>
        {value || "—"}
      </div>
    </div>
  );
}

function JsonList({ label, value }: { label: string; value: unknown }) {
  const items = Array.isArray(value) ? value : [];

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={labelStyle}>{label}</div>
      {items.length ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {items.map((item, index) => (
            <span
              key={`${String(item)}-${index}`}
              style={{
                fontSize: 12,
                padding: "4px 8px",
                borderRadius: 999,
                background: "#eef2ff",
                color: "#3730a3",
                border: "1px solid #c7d2fe",
              }}
            >
              {String(item)}
            </span>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 14, color: "#64748b" }}>—</div>
      )}
    </div>
  );
}

export default function CompetitiveSetupIntelligence({ accountId, isPartnerMode = false, onChanged }: Props) {
  const [profile, setProfile] = useState<AccountMarketProfile | null>(null);
  const [brandGuidance, setBrandGuidance] = useState<AccountBrandGuidance | null>(null);
  const [roles, setRoles] = useState<AccountEntityRole[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingBrandGuidance, setIsSavingBrandGuidance] = useState(false);
  const [isGeneratingMarketProfile, setIsGeneratingMarketProfile] = useState(false);
  const [isGeneratingEntityRoles, setIsGeneratingEntityRoles] = useState(false);
  const [isSavingRole, setIsSavingRole] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  const [primaryCategory, setPrimaryCategory] = useState("");
  const [plainLanguageDescription, setPlainLanguageDescription] = useState("");
  const [directDefinition, setDirectDefinition] = useState("");
  const [adjacentDefinition, setAdjacentDefinition] = useState("");
  const [platformDefinition, setPlatformDefinition] = useState("");
  const [exclusionDefinition, setExclusionDefinition] = useState("");
  const [buyerTypes, setBuyerTypes] = useState("");
  const [jobsToBeDone, setJobsToBeDone] = useState("");
  const [knownDirect, setKnownDirect] = useState("");
  const [knownAdjacent, setKnownAdjacent] = useState("");
  const [knownPlatforms, setKnownPlatforms] = useState("");
  const [knownExclusions, setKnownExclusions] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  const [guidanceTitle, setGuidanceTitle] = useState("");
  const [rawGuidance, setRawGuidance] = useState("");
  const [brandVoiceSummary, setBrandVoiceSummary] = useState("");
  const [toneDo, setToneDo] = useState("");
  const [toneDont, setToneDont] = useState("");
  const [preferredTerms, setPreferredTerms] = useState("");
  const [bannedTerms, setBannedTerms] = useState("");
  const [approvedClaims, setApprovedClaims] = useState("");
  const [claimsToAvoid, setClaimsToAvoid] = useState("");
  const [audienceNotes, setAudienceNotes] = useState("");
  const [positioningNotes, setPositioningNotes] = useState("");
  const [differentiators, setDifferentiators] = useState("");
  const [ctaPreferences, setCtaPreferences] = useState("");
  const [complianceNotes, setComplianceNotes] = useState("");
  const [oeGuardrails, setOeGuardrails] = useState("");
  const [promptGenerationNotes, setPromptGenerationNotes] = useState("");
  const [marketProfileNotes, setMarketProfileNotes] = useState("");
  const [applyToOpportunityEngine, setApplyToOpportunityEngine] = useState(true);
  const [applyToPromptGeneration, setApplyToPromptGeneration] = useState(true);
  const [applyToMarketProfile, setApplyToMarketProfile] = useState(true);

  const [rawEntityName, setRawEntityName] = useState("");
  const [normalizedEntityName, setNormalizedEntityName] = useState("");
  const [parentEntityName, setParentEntityName] = useState("");

  function applyCanonicalCompetitorPreset() {
    setEntityRole("direct_competitor");
    setScoreTreatment("include_primary");
    setRationale((current) =>
      current.trim()
        ? current
        : "Raw extracted entity appears to be a service-area, alias, or naming variant of the canonical competitor."
    );
  }

  function applySuppressEverywherePreset() {
    setEntityRole("suppress");
    setScoreTreatment("suppress_everywhere");
    setRationale((current) =>
      current.trim()
        ? current
        : "Entity is invalid, irrelevant, generic, or should not appear in customer-facing scoring, opportunities, or action briefs."
    );
  }

  function getEntityGovernanceImpact(role: AccountEntityRole) {
    if (role.score_treatment === "suppress_everywhere" || role.entity_role === "suppress") {
      return {
        label: "Suppressed everywhere",
        description: "Hidden from scoring, opportunity surfaces, and action brief generation.",
        background: "#fff1f2",
        color: "#be123c",
        border: "#fecaca",
      };
    }

    if (role.score_treatment === "include_primary") {
      return {
        label: "Primary scoring + briefs",
        description: "Included in scoring, Opportunity Engine surfaces, and governed action briefs.",
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "#bfdbfe",
      };
    }

    if (role.score_treatment === "include_secondary") {
      return {
        label: "Secondary scoring + briefs",
        description: "Included as a secondary governed entity for scoring and action brief context.",
        background: "#f8fafc",
        color: "#334155",
        border: "#cbd5e1",
      };
    }

    if (role.score_treatment === "show_only") {
      return {
        label: "Show only",
        description: "Visible for admin/customer context but not treated as a primary scoring target.",
        background: "#fefce8",
        color: "#854d0e",
        border: "#fde68a",
      };
    }

    return {
      label: "Review treatment",
      description: "Treatment is not mapped to a known governance impact.",
      background: "#f8fafc",
      color: "#475569",
      border: "#cbd5e1",
    };
  }
  const [entityRole, setEntityRole] = useState<EntityRole>("adjacent_competitor");
  const [scoreTreatment, setScoreTreatment] = useState<ScoreTreatment>("include_secondary");
  const [rationale, setRationale] = useState("");

  const scorableRoles = useMemo(
    () => roles.filter((role) => ["include_primary", "include_secondary"].includes(role.score_treatment)),
    [roles]
  );

  function hydrateProfileForm(nextProfile: AccountMarketProfile | null) {
    setPrimaryCategory(nextProfile?.primary_category || "");
    setPlainLanguageDescription(nextProfile?.plain_language_description || "");
    setDirectDefinition(nextProfile?.direct_competitor_definition || "");
    setAdjacentDefinition(nextProfile?.adjacent_competitor_definition || "");
    setPlatformDefinition(nextProfile?.platform_partner_definition || "");
    setExclusionDefinition(nextProfile?.exclusion_definition || "");
    setBuyerTypes(toCsv(nextProfile?.buyer_types));
    setJobsToBeDone(toCsv(nextProfile?.jobs_to_be_done));
    setKnownDirect(toCsv(nextProfile?.known_direct_competitors));
    setKnownAdjacent(toCsv(nextProfile?.known_adjacent_competitors));
    setKnownPlatforms(toCsv(nextProfile?.known_platforms_partners));
    setKnownExclusions(toCsv(nextProfile?.known_exclusions));
    setAdminNotes(nextProfile?.admin_notes || "");
  }


  function hydrateBrandGuidanceForm(nextGuidance: AccountBrandGuidance | null) {
    setGuidanceTitle(nextGuidance?.guidance_title || "");
    setRawGuidance(nextGuidance?.raw_guidance || "");
    setBrandVoiceSummary(nextGuidance?.brand_voice_summary || "");
    setToneDo(toCsv(nextGuidance?.tone_do));
    setToneDont(toCsv(nextGuidance?.tone_dont));
    setPreferredTerms(toCsv(nextGuidance?.preferred_terms));
    setBannedTerms(toCsv(nextGuidance?.banned_terms));
    setApprovedClaims(toCsv(nextGuidance?.approved_claims));
    setClaimsToAvoid(toCsv(nextGuidance?.claims_to_avoid));
    setAudienceNotes(nextGuidance?.audience_notes || "");
    setPositioningNotes(nextGuidance?.positioning_notes || "");
    setDifferentiators(toCsv(nextGuidance?.differentiators));
    setCtaPreferences(nextGuidance?.cta_preferences || "");
    setComplianceNotes(nextGuidance?.compliance_notes || "");
    setOeGuardrails(nextGuidance?.oe_guardrails || "");
    setPromptGenerationNotes(nextGuidance?.prompt_generation_notes || "");
    setMarketProfileNotes(nextGuidance?.market_profile_notes || "");
    setApplyToOpportunityEngine(nextGuidance?.apply_to_opportunity_engine ?? true);
    setApplyToPromptGeneration(nextGuidance?.apply_to_prompt_generation ?? true);
    setApplyToMarketProfile(nextGuidance?.apply_to_market_profile ?? true);
  }

  async function loadCompetitiveSetup() {
    if (!accountId) return;

    setIsLoading(true);
    setStatus("");

    try {
      const [latestProfile, latestBrandGuidance, entityRoles] = await Promise.all([
        getLatestMarketProfile(accountId),
        getLatestBrandGuidance(accountId),
        getEntityRoles(accountId),
      ]);

      setProfile(latestProfile);
      setBrandGuidance(latestBrandGuidance);
      setRoles(entityRoles);
      hydrateProfileForm(latestProfile);
      hydrateBrandGuidanceForm(latestBrandGuidance);
    } catch (error: any) {
      console.error("[CompetitiveSetupIntelligence] Failed to load setup", error);
      setStatus(error?.message || "Failed to load competitive setup.");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadCompetitiveSetup();
  }, [accountId]);

  async function refreshAfterSetupChange() {
    await loadCompetitiveSetup();
    await onChanged?.();
  }

  async function handleGenerateMarketProfile() {
    if (!accountId) return;

    const confirmed = window.confirm(
      "Generate a new agent market profile for this account? This will load the generated profile into the review form but will not approve it."
    );

    if (!confirmed) return;

    setIsGeneratingMarketProfile(true);
    setStatus("");

    try {
      const generatedProfile = await generateMarketProfileForAccount(accountId);
      setProfile(generatedProfile);
      hydrateProfileForm(generatedProfile);
      setIsEditingProfile(true);
      await onChanged?.();
      setStatus("Generated market profile. Review, edit, then save or approve.");
    } catch (error: any) {
      console.error("[CompetitiveSetup] Generate market profile failed:", error);
      setStatus(error?.message || "Failed to generate market profile.");
      alert(error?.message || "Failed to generate market profile.");
    } finally {
      setIsGeneratingMarketProfile(false);
    }
  }

  async function handleGenerateEntityRoleSeeds() {
    if (!accountId) return;

    if (!profile?.id) {
      setStatus("Generate or save a market profile before generating entity role seeds.");
      return;
    }

    const confirmed = window.confirm(
      "Generate agent entity role seeds for this account? New non-conflicting seeds will be added for review."
    );

    if (!confirmed) return;

    setIsGeneratingEntityRoles(true);
    setStatus("");

    try {
      const payload = await generateEntityRoleSeedsForAccount(accountId, profile.id);

      if (Array.isArray(payload.roles)) {
        setRoles((currentRoles) => {
          const byId = new Map(currentRoles.map((role) => [role.id, role]));

          for (const role of payload.roles) {
            if (role?.id) {
              byId.set(role.id, role);
            }
          }

          return Array.from(byId.values()).sort((a, b) =>
            String(a.raw_entity_name || "").localeCompare(String(b.raw_entity_name || ""))
          );
        });

        await refreshAfterSetupChange();
        setStatus(`Generated ${payload.roles.length} entity role seed${payload.roles.length === 1 ? "" : "s"}. Review before approving/running.`);
      } else {
        await refreshAfterSetupChange();
        setStatus("Entity role seed generation completed. Refreshed saved roles from database.");
      }
    } catch (error: any) {
      console.error("[CompetitiveSetup] Generate entity role seeds failed:", error);
      setStatus(error?.message || "Failed to generate entity role seeds.");
      alert(error?.message || "Failed to generate entity role seeds.");
    } finally {
      setIsGeneratingEntityRoles(false);
    }
  }

  async function handleSaveProfile() {
    setIsSavingProfile(true);
    setStatus("");

    try {
      const saved = await saveMarketProfile({
        profileId: profile?.id || null,
        account_id: accountId,
        status: profile?.status || "draft",
        primary_category: primaryCategory,
        plain_language_description: plainLanguageDescription,
        direct_competitor_definition: directDefinition,
        adjacent_competitor_definition: adjacentDefinition,
        platform_partner_definition: platformDefinition,
        exclusion_definition: exclusionDefinition,
        buyer_types: parseCsv(buyerTypes),
        jobs_to_be_done: parseCsv(jobsToBeDone),
        known_direct_competitors: parseCsv(knownDirect),
        known_adjacent_competitors: parseCsv(knownAdjacent),
        known_platforms_partners: parseCsv(knownPlatforms),
        known_exclusions: parseCsv(knownExclusions),
        confidence_score: profile?.confidence_score ?? null,
        admin_notes: adminNotes,
      });

      setProfile(saved);
      hydrateProfileForm(saved);
      setIsEditingProfile(false);
      await onChanged?.();
      setStatus("Market profile saved.");
    } catch (error: any) {
      console.error("[CompetitiveSetupIntelligence] Failed to save profile", error);
      setStatus(error?.message || "Failed to save market profile.");
    } finally {
      setIsSavingProfile(false);
    }
  }

  async function handleApproveProfile() {
    if (!profile) return;

    try {
      const updated = await updateMarketProfileStatus({
        profileId: profile.id,
        account_id: accountId,
        status: "approved",
      });

      setProfile(updated);
      hydrateProfileForm(updated);
      await onChanged?.();
      setStatus("Market profile approved.");
    } catch (error: any) {
      console.error("[CompetitiveSetupIntelligence] Failed to approve profile", error);
      setStatus(error?.message || "Failed to approve market profile.");
    }
  }

  async function handleSaveBrandGuidance() {
    setIsSavingBrandGuidance(true);
    setStatus("");

    try {
      const saved = await saveBrandGuidance({
        guidanceId: brandGuidance?.id || null,
        account_id: accountId,
        status: brandGuidance?.status || "draft",
        guidance_title: guidanceTitle,
        raw_guidance: rawGuidance,
        brand_voice_summary: brandVoiceSummary,
        tone_do: parseCsv(toneDo),
        tone_dont: parseCsv(toneDont),
        preferred_terms: parseCsv(preferredTerms),
        banned_terms: parseCsv(bannedTerms),
        approved_claims: parseCsv(approvedClaims),
        claims_to_avoid: parseCsv(claimsToAvoid),
        audience_notes: audienceNotes,
        positioning_notes: positioningNotes,
        differentiators: parseCsv(differentiators),
        cta_preferences: ctaPreferences,
        compliance_notes: complianceNotes,
        oe_guardrails: oeGuardrails,
        prompt_generation_notes: promptGenerationNotes,
        market_profile_notes: marketProfileNotes,
        apply_to_opportunity_engine: applyToOpportunityEngine,
        apply_to_prompt_generation: applyToPromptGeneration,
        apply_to_market_profile: applyToMarketProfile,
      });

      setBrandGuidance(saved);
      hydrateBrandGuidanceForm(saved);
      await onChanged?.();
      setStatus("Brand guidance saved.");
    } catch (error: any) {
      console.error("[CompetitiveSetupIntelligence] Failed to save brand guidance", error);
      setStatus(error?.message || "Failed to save brand guidance.");
    } finally {
      setIsSavingBrandGuidance(false);
    }
  }

  async function handleCreateRole() {
    if (!rawEntityName.trim()) {
      setStatus("Raw entity name is required.");
      return;
    }

    const normalized = normalizedEntityName.trim() || rawEntityName.trim();

    setIsSavingRole(true);
    setStatus("");

    try {
      const created = await createEntityRole({
        account_id: accountId,
        market_profile_id: profile?.id || null,
        raw_entity_name: rawEntityName.trim(),
        normalized_entity_name: normalized,
        parent_entity_name: parentEntityName.trim() || null,
        entity_role: entityRole,
        score_treatment: scoreTreatment,
        confidence_score: null,
        rationale: rationale.trim() || null,
      });

      setRoles((current) => [...current, created].sort((a, b) => a.raw_entity_name.localeCompare(b.raw_entity_name)));
      setRawEntityName("");
      setNormalizedEntityName("");
      setParentEntityName("");
      setEntityRole("adjacent_competitor");
      setScoreTreatment("include_secondary");
      setRationale("");
      await onChanged?.();
      setStatus("Entity role added.");
    } catch (error: any) {
      console.error("[CompetitiveSetupIntelligence] Failed to create entity role", error);
      setStatus(error?.message || "Failed to add entity role.");
    } finally {
      setIsSavingRole(false);
    }
  }

  async function handleDeactivateRole(role: AccountEntityRole) {
    const confirmed = window.confirm(`Deactivate entity role for ${role.raw_entity_name}?`);
    if (!confirmed) return;

    try {
      await updateEntityRole(role.id, { is_active: false });
      setRoles((current) => current.filter((item) => item.id !== role.id));
      await onChanged?.();
      setStatus(`Deactivated ${role.raw_entity_name}.`);
    } catch (error: any) {
      console.error("[CompetitiveSetupIntelligence] Failed to deactivate entity role", error);
      setStatus(error?.message || "Failed to deactivate entity role.");
    }
  }

  return (
    <div
      style={{
        border: "1px solid #c7d2fe",
        background: "#f8fafc",
        borderRadius: 16,
        padding: 18,
        marginTop: 24,
        marginBottom: 24,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.08, color: "#4f46e5", fontWeight: 800 }}>
            Competitive Setup Intelligence
          </div>
          <h2 style={{ margin: "4px 0 6px", fontSize: 22, color: "#0f172a" }}>
            Market profile & entity governance
          </h2>
          <p style={{ margin: 0, color: "#475569", fontSize: 14, maxWidth: 760 }}>
            Manual V1 shell for reviewing how Toren should interpret this account’s market, competitors, platforms,
            partners, and suppressible entities before agent automation is added.
          </p>
        </div>

        <button
          type="button"
          onClick={loadCompetitiveSetup}
          disabled={isLoading}
          style={{
            border: "1px solid #cbd5e1",
            background: "white",
            borderRadius: 10,
            padding: "8px 12px",
            cursor: isLoading ? "not-allowed" : "pointer",
            fontWeight: 700,
          }}
        >
          {isLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {status && (
        <div
          style={{
            marginTop: 14,
            padding: 10,
            borderRadius: 10,
            background: "#eef2ff",
            color: "#3730a3",
            fontSize: 13,
            border: "1px solid #c7d2fe",
          }}
        >
          {status}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 18,
          marginTop: 18,
        }}
      >
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <h3 style={{ margin: 0, color: "#0f172a" }}>Market Profile</h3>
              <button
                type="button"
                onClick={handleGenerateMarketProfile}
                disabled={isGeneratingMarketProfile}
                style={{
                  border: "1px solid #bfdbfe",
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: isGeneratingMarketProfile ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {isGeneratingMarketProfile ? "Generating..." : "Generate Market Profile"}
              </button>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => setIsEditingProfile((current) => !current)}
                style={{
                  border: "1px solid #cbd5e1",
                  background: "white",
                  color: "#334155",
                  borderRadius: 10,
                  padding: "7px 10px",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                {isEditingProfile || !profile ? "View" : "Edit"}
              </button>
              {profile && profile.status !== "approved" && (
                <button
                  type="button"
                  onClick={handleApproveProfile}
                  style={{
                    border: "1px solid #22c55e",
                    background: "#dcfce7",
                    color: "#166534",
                    borderRadius: 10,
                    padding: "7px 10px",
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                >
                  Approve
                </button>
              )}
            </div>
          </div>

          {isEditingProfile || !profile ? (
            <div style={{ display: "grid", gap: 10 }}>
              <label style={fieldLabelStyle}>
                Primary category
                <input value={primaryCategory} onChange={(event) => setPrimaryCategory(event.target.value)} placeholder="Example: Advertising & Marketing Agency" style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Plain-language description
                <textarea value={plainLanguageDescription} onChange={(event) => setPlainLanguageDescription(event.target.value)} placeholder="Describe what this business does in plain language." rows={3} style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Direct competitor definition
                <textarea value={directDefinition} onChange={(event) => setDirectDefinition(event.target.value)} placeholder="Define which entities should count as direct competitors." rows={3} style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Adjacent competitor definition
                <textarea value={adjacentDefinition} onChange={(event) => setAdjacentDefinition(event.target.value)} placeholder="Define adjacent competitors or overlapping alternatives." rows={3} style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Platform / partner definition
                <textarea value={platformDefinition} onChange={(event) => setPlatformDefinition(event.target.value)} placeholder="Define platforms, partners, tools, or ecosystem entities." rows={3} style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Exclusion definition
                <textarea value={exclusionDefinition} onChange={(event) => setExclusionDefinition(event.target.value)} placeholder="Define entities that should be suppressed or ignored." rows={3} style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Buyer types
                <input value={buyerTypes} onChange={(event) => setBuyerTypes(event.target.value)} placeholder="Comma separated, e.g. CMOs, business owners, operations leaders" style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Jobs to be done
                <input value={jobsToBeDone} onChange={(event) => setJobsToBeDone(event.target.value)} placeholder="Comma separated customer needs or buying jobs" style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Known direct competitors
                <input value={knownDirect} onChange={(event) => setKnownDirect(event.target.value)} placeholder="Comma separated direct competitors" style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Known adjacent competitors
                <input value={knownAdjacent} onChange={(event) => setKnownAdjacent(event.target.value)} placeholder="Comma separated adjacent competitors" style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Known platforms / partners
                <input value={knownPlatforms} onChange={(event) => setKnownPlatforms(event.target.value)} placeholder="Comma separated platforms, partners, or tools" style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Known exclusions
                <input value={knownExclusions} onChange={(event) => setKnownExclusions(event.target.value)} placeholder="Comma separated entities to exclude or suppress" style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Admin notes
                <textarea value={adminNotes} onChange={(event) => setAdminNotes(event.target.value)} placeholder="Optional internal notes about this setup." rows={3} style={inputStyle} />
              </label>
              <button
                type="button"
                onClick={handleSaveProfile}
                disabled={isSavingProfile}
                style={{
                  border: "1px solid #4f46e5",
                  background: "#4f46e5",
                  color: "white",
                  borderRadius: 10,
                  padding: "10px 12px",
                  cursor: isSavingProfile ? "not-allowed" : "pointer",
                  fontWeight: 800,
                }}
              >
                {isSavingProfile ? "Saving..." : "Save Market Profile"}
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, padding: "4px 8px", borderRadius: 999, background: "#f1f5f9", color: "#334155" }}>
                  Status: {profile.status}
                </span>
                <span style={{ fontSize: 12, padding: "4px 8px", borderRadius: 999, background: "#f1f5f9", color: "#334155" }}>
                  Source: {profile.source}
                </span>
                {profile.confidence_score !== null && (
                  <span style={{ fontSize: 12, padding: "4px 8px", borderRadius: 999, background: "#f1f5f9", color: "#334155" }}>
                    Confidence: {profile.confidence_score}
                  </span>
                )}
              </div>

              <FieldValue label="Primary category" value={profile.primary_category} />
              <FieldValue label="Plain-language description" value={profile.plain_language_description} />
              <FieldValue label="Direct competitor definition" value={profile.direct_competitor_definition} />
              <FieldValue label="Adjacent competitor definition" value={profile.adjacent_competitor_definition} />
              <FieldValue label="Platform / partner definition" value={profile.platform_partner_definition} />
              <FieldValue label="Exclusion definition" value={profile.exclusion_definition} />
              <JsonList label="Buyer types" value={profile.buyer_types} />
              <JsonList label="Jobs to be done" value={profile.jobs_to_be_done} />
            </>
          )}
        </div>

        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
          <h3 style={{ margin: "0 0 6px", color: "#0f172a" }}>Add Entity Role / Canonical Mapping</h3>
          <p style={{ margin: "0 0 12px", color: "#64748b", fontSize: 13, lineHeight: 1.45 }}>
            Use this when AI extracts a messy, local, aliased, or invalid entity. Raw is what AI saw. Canonical is the verified entity Toren should display and use downstream.
          </p>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 12
            }}
          >
            <button
              type="button"
              onClick={applyCanonicalCompetitorPreset}
              style={{
                border: "1px solid #bfdbfe",
                background: "#eff6ff",
                color: "#1d4ed8",
                borderRadius: 999,
                padding: "7px 10px",
                fontSize: 12,
                fontWeight: 800,
                cursor: "pointer"
              }}
            >
              Canonicalize as competitor
            </button>
            <button
              type="button"
              onClick={applySuppressEverywherePreset}
              style={{
                border: "1px solid #fecaca",
                background: "#fff1f2",
                color: "#be123c",
                borderRadius: 999,
                padding: "7px 10px",
                fontSize: 12,
                fontWeight: 800,
                cursor: "pointer"
              }}
            >
              Suppress everywhere
            </button>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <label style={{ display: "grid", gap: 4, color: "#334155", fontSize: 12, fontWeight: 800 }}>
              Raw extracted entity
              <input value={rawEntityName} onChange={(event) => setRawEntityName(event.target.value)} placeholder="Example: Acura of Poughkeepsie" style={inputStyle} />
            </label>
            <label style={{ display: "grid", gap: 4, color: "#334155", fontSize: 12, fontWeight: 800 }}>
              Canonical entity Toren should use
              <input value={normalizedEntityName} onChange={(event) => setNormalizedEntityName(event.target.value)} placeholder="Example: Friendly Acura of Middletown" style={inputStyle} />
            </label>
            <label style={{ display: "grid", gap: 4, color: "#334155", fontSize: 12, fontWeight: 800 }}>
              Parent entity, optional
              <input value={parentEntityName} onChange={(event) => setParentEntityName(event.target.value)} placeholder="Parent entity, if applicable" style={inputStyle} />
            </label>

            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
              <select value={entityRole} onChange={(event) => setEntityRole(event.target.value as EntityRole)} style={inputStyle}>
                {ENTITY_ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
              </select>

              <select value={scoreTreatment} onChange={(event) => setScoreTreatment(event.target.value as ScoreTreatment)} style={inputStyle}>
                {SCORE_TREATMENTS.map((treatment) => <option key={treatment} value={treatment}>{treatment}</option>)}
              </select>
            </div>

            <textarea value={rationale} onChange={(event) => setRationale(event.target.value)} placeholder="Rationale, optional" rows={3} style={inputStyle} />

            <button
              type="button"
              onClick={handleCreateRole}
              disabled={isSavingRole}
              style={{
                border: "1px solid #4f46e5",
                background: "#4f46e5",
                color: "white",
                borderRadius: 10,
                padding: "10px 12px",
                cursor: isSavingRole ? "not-allowed" : "pointer",
                fontWeight: 800,
              }}
            >
              {isSavingRole ? "Saving..." : "Save Entity Mapping"}
            </button>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 18, background: "white", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ margin: "0 0 6px", color: "#0f172a" }}>Brand Guidance</h3>
          <p style={{ margin: 0, color: "#64748b", fontSize: 13, lineHeight: 1.45 }}>
            Optional brand voice, tone, terminology, claims, and messaging guidance for future agentic onboarding,
            prompt generation, Opportunity Engine action briefs, and canonical entity governance.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>
          <label style={fieldLabelStyle}>
            Guidance title
            <input value={guidanceTitle} onChange={(event) => setGuidanceTitle(event.target.value)} placeholder="Example: Brand Guide V1" style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            Brand voice summary
            <input value={brandVoiceSummary} onChange={(event) => setBrandVoiceSummary(event.target.value)} placeholder="Short summary of the brand voice." style={inputStyle} />
          </label>
        </div>

        <label style={{ ...fieldLabelStyle, marginTop: 10 }}>
          Raw guidance
          <textarea value={rawGuidance} onChange={(event) => setRawGuidance(event.target.value)} placeholder="Paste brand guide excerpts, tone rules, messaging rules, sales partner notes, or customer-specific guidance here." rows={5} style={inputStyle} />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12, marginTop: 10 }}>
          <label style={fieldLabelStyle}>
            Tone do
            <input value={toneDo} onChange={(event) => setToneDo(event.target.value)} placeholder="Comma separated tone guidance to follow" style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            Tone don't
            <input value={toneDont} onChange={(event) => setToneDont(event.target.value)} placeholder="Comma separated tone guidance to avoid" style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            Preferred terms
            <input value={preferredTerms} onChange={(event) => setPreferredTerms(event.target.value)} placeholder="Comma separated approved terms" style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            Banned terms
            <input value={bannedTerms} onChange={(event) => setBannedTerms(event.target.value)} placeholder="Comma separated terms to avoid" style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            Approved claims
            <input value={approvedClaims} onChange={(event) => setApprovedClaims(event.target.value)} placeholder="Comma separated verified claims" style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            Claims to avoid
            <input value={claimsToAvoid} onChange={(event) => setClaimsToAvoid(event.target.value)} placeholder="Comma separated claims to avoid" style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            Differentiators
            <input value={differentiators} onChange={(event) => setDifferentiators(event.target.value)} placeholder="Comma separated verified differentiators" style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            CTA preferences
            <input value={ctaPreferences} onChange={(event) => setCtaPreferences(event.target.value)} placeholder="Preferred calls to action" style={inputStyle} />
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12, marginTop: 10 }}>
          <label style={fieldLabelStyle}>
            Audience notes
            <textarea value={audienceNotes} onChange={(event) => setAudienceNotes(event.target.value)} placeholder="Notes about the audience, buyers, or stakeholders." rows={3} style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            Positioning notes
            <textarea value={positioningNotes} onChange={(event) => setPositioningNotes(event.target.value)} placeholder="Positioning, differentiation, or messaging notes." rows={3} style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            Compliance notes
            <textarea value={complianceNotes} onChange={(event) => setComplianceNotes(event.target.value)} placeholder="Compliance, legal, or claims guidance." rows={3} style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            Opportunity Engine guardrails
            <textarea value={oeGuardrails} onChange={(event) => setOeGuardrails(event.target.value)} placeholder="Guidance for Opportunity Engine recommendations and action briefs." rows={3} style={inputStyle} />
          </label>

          <div
            style={{
              marginTop: 14,
              marginBottom: 14,
              border: "1px solid #bfdbfe",
              background: "#eff6ff",
              borderRadius: 14,
              padding: 14,
              color: "#1e3a8a",
              fontSize: 13,
              lineHeight: 1.5
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>
              Entity governance guide
            </div>
            <div>
              Use <strong>Raw entity</strong> for the exact name AI extracted. Use <strong>Normalized entity</strong> for the verified canonical name Toren should display and score.
            </div>
            <div style={{ marginTop: 6 }}>
              Example: <strong>Acura of Poughkeepsie</strong> should normalize to <strong>Friendly Acura of Middletown</strong> if it is a service-area variant, not a separate competitor.
            </div>
            <div style={{ marginTop: 6 }}>
              Only use <strong>suppress_everywhere</strong> when the entity is truly invalid, irrelevant, generic, or should not appear anywhere. If it is a real competitor under a messy local name, canonicalize it instead.
            </div>
          </div>
          <label style={fieldLabelStyle}>
            Prompt generation notes
            <textarea value={promptGenerationNotes} onChange={(event) => setPromptGenerationNotes(event.target.value)} placeholder="Guidance for future prompt generation." rows={3} style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            Market profile notes
            <textarea value={marketProfileNotes} onChange={(event) => setMarketProfileNotes(event.target.value)} placeholder="Guidance for market profile interpretation." rows={3} style={inputStyle} />
          </label>
        </div>

        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 12, color: "#334155", fontSize: 13 }}>
          <label><input type="checkbox" checked={applyToOpportunityEngine} onChange={(event) => setApplyToOpportunityEngine(event.target.checked)} /> Apply to OE</label>
          <label><input type="checkbox" checked={applyToPromptGeneration} onChange={(event) => setApplyToPromptGeneration(event.target.checked)} /> Apply to prompt generation</label>
          <label><input type="checkbox" checked={applyToMarketProfile} onChange={(event) => setApplyToMarketProfile(event.target.checked)} /> Apply to market profile</label>
        </div>

        <button
          type="button"
          onClick={handleSaveBrandGuidance}
          disabled={isSavingBrandGuidance}
          style={{
            marginTop: 12,
            border: "1px solid #4f46e5",
            background: "#4f46e5",
            color: "white",
            borderRadius: 10,
            padding: "10px 12px",
            cursor: isSavingBrandGuidance ? "not-allowed" : "pointer",
            fontWeight: 800,
          }}
        >
          {isSavingBrandGuidance ? "Saving..." : "Save Brand Guidance"}
        </button>
      </div>

      <div style={{ marginTop: 18, background: "white", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <h3 style={{ margin: 0, color: "#0f172a" }}>Entity Roles</h3>
            {!isPartnerMode && (
              <button
                type="button"
                onClick={handleGenerateEntityRoleSeeds}
                disabled={isGeneratingEntityRoles || !profile?.id}
                style={{
                  border: "1px solid #bfdbfe",
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: isGeneratingEntityRoles || !profile?.id ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {isGeneratingEntityRoles ? "Generating..." : "Generate Entity Role Seeds"}
              </button>
            )}
          </div>
          <div style={{ fontSize: 13, color: "#475569" }}>
            Scorable entities: <strong>{scorableRoles.length}</strong> / Active roles: <strong>{roles.length}</strong>
          </div>
        </div>

        {roles.length ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                gap: 10,
                marginBottom: 14,
              }}
            >
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 12, background: "#f8fafc" }}>
                <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Canonical mappings</div>
                <div style={{ fontSize: 22, color: "#0f172a", fontWeight: 900 }}>
                  {roles.filter((role) => role.raw_entity_name !== role.normalized_entity_name).length}
                </div>
              </div>
              <div style={{ border: "1px solid #bfdbfe", borderRadius: 12, padding: 12, background: "#eff6ff" }}>
                <div style={{ fontSize: 12, color: "#1d4ed8", fontWeight: 800 }}>Primary included</div>
                <div style={{ fontSize: 22, color: "#1e3a8a", fontWeight: 900 }}>
                  {roles.filter((role) => role.score_treatment === "include_primary").length}
                </div>
              </div>
              <div style={{ border: "1px solid #fecaca", borderRadius: 12, padding: 12, background: "#fff1f2" }}>
                <div style={{ fontSize: 12, color: "#be123c", fontWeight: 800 }}>Suppressed</div>
                <div style={{ fontSize: 22, color: "#881337", fontWeight: 900 }}>
                  {roles.filter((role) => role.score_treatment === "suppress_everywhere" || role.entity_role === "suppress").length}
                </div>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
            <table style={{ minWidth: 920, width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                  <th style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>Action</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>Raw Extracted</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>Canonical</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>Parent</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>Role</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>Treatment</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>Impact</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr key={role.id}>
                    <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9" }}>
                      <button
                        type="button"
                        onClick={() => handleDeactivateRole(role)}
                        style={{
                          border: "1px solid #fecaca",
                          background: "#fff1f2",
                          color: "#be123c",
                          borderRadius: 8,
                          padding: "6px 8px",
                          cursor: "pointer",
                          fontWeight: 700,
                        }}
                      >
                        Deactivate
                      </button>
                    </td>
                    <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9", fontWeight: 700 }}>{role.raw_entity_name}</td>
                    <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9" }}>{role.normalized_entity_name}</td>
                    <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9" }}>{role.parent_entity_name || "—"}</td>
                    <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9" }}>{role.entity_role}</td>
                    <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9" }}>{role.score_treatment}</td>
                    <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9", minWidth: 210 }}>
                      {(() => {
                        const impact = getEntityGovernanceImpact(role);
                        return (
                          <div>
                            <span
                              title={impact.description}
                              style={{
                                display: "inline-flex",
                                border: `1px solid ${impact.border}`,
                                background: impact.background,
                                color: impact.color,
                                borderRadius: 999,
                                padding: "4px 8px",
                                fontSize: 12,
                                fontWeight: 800,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {impact.label}
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9" }}>{role.confidence_score ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        ) : (
          <div style={{ color: "#64748b", fontSize: 14 }}>
            No active entity roles found yet.
          </div>
        )}
      </div>
    </div>
  );
}
